const steps = [
  {
    number: "01",
    title: "Wood Selection",
    desc: "We handpick only the finest seasoned hardwoods — teak, walnut, and mahogany — sourced from sustainable forests. Each plank is inspected for grain, density, and durability.",
    image: "https://readdy.ai/api/search-image?query=master%20craftsman%20selecting%20premium%20hardwood%20timber%20teak%20mahogany%20workshop%20heritage%20furniture%20close%20up%20hands%20inspecting%20wood%20grain%20warm%20golden%20lighting%20traditional%20artisan&width=400&height=320&seq=craft1&orientation=landscape",
    icon: "ri-plant-line",
  },
  {
    number: "02",
    title: "Hand Carving",
    desc: "Our master carvers transform raw wood into intricate masterpieces. Using chisels and gouges unchanged since 1870, they sculpt floral motifs, geometric patterns, and ornate reliefs.",
    image: "https://readdy.ai/api/search-image?query=master%20craftsman%20hand%20carving%20intricate%20wood%20pattern%20chisel%20close%20up%20dark%20mahogany%20furniture%20traditional%20heritage%20artisan%20workshop%20detailed%20work%20warm%20lighting%20Pakistan&width=400&height=320&seq=craft2&orientation=landscape",
    icon: "ri-tools-line",
  },
  {
    number: "03",
    title: "Assembly & Joinery",
    desc: "We employ traditional mortise-and-tenon joinery techniques — the same methods used by our founding craftsmen. No nails or staples; only precision-fit wooden joints built to endure generations.",
    image: "https://readdy.ai/api/search-image?query=furniture%20assembly%20traditional%20mortise%20tenon%20joinery%20craftsman%20hands%20fitting%20wood%20joints%20heritage%20workshop%20close%20up%20precision%20carpentry%20warm%20lighting%20artisan%20skill&width=400&height=320&seq=craft3&orientation=landscape",
    icon: "ri-puzzle-2-line",
  },
  {
    number: "04",
    title: "Polishing & Finish",
    desc: "Each piece undergoes multiple rounds of hand-sanding, staining, and lacquering. Our signature deep-brown polish is applied in up to twelve coats, revealing the wood's natural beauty.",
    image: "https://readdy.ai/api/search-image?query=craftsman%20hand%20polishing%20furniture%20dark%20mahogany%20wood%20finish%20close%20up%20smooth%20polishing%20cloth%20heritage%20furniture%20workshop%20warm%20golden%20lighting%20luxury%20quality%20artisan%20finishing&width=400&height=320&seq=craft4&orientation=landscape",
    icon: "ri-brush-line",
  },
  {
    number: "05",
    title: "Quality Inspection",
    desc: "Before leaving our workshop, every piece faces a rigorous quality inspection by our senior artisans. Only furniture meeting our 150-year-old standards of excellence earns the M. Hayat &amp; Co. mark.",
    image: "https://readdy.ai/api/search-image?query=quality%20inspection%20senior%20craftsman%20examining%20finished%20furniture%20piece%20premium%20heritage%20workshop%20close%20up%20careful%20inspection%20luxury%20standard%20attention%20to%20detail%20warm%20lighting&width=400&height=320&seq=craft5&orientation=landscape",
    icon: "ri-award-line",
  },
];

export default function CraftsmanshipSection() {
  return (
    <section id="craftsmanship" className="bg-brand-darkbrown py-24 px-6 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="font-display text-brand-gold text-xs tracking-[4px] uppercase block mb-4">The Art Behind the Piece</span>
          <h2 className="font-serif text-4xl md:text-5xl text-brand-cream font-bold">
            Our <em className="text-brand-goldlight">Craftsmanship</em>
          </h2>
          <p className="font-serif text-brand-cream/60 mt-3 italic text-lg">
            Every Piece Tells a Story
          </p>
          <div className="flex items-center justify-center gap-4 mt-5">
            <span className="h-px w-16 bg-brand-gold/40" />
            <i className="ri-star-fill text-brand-gold text-xs" />
            <span className="h-px w-16 bg-brand-gold/40" />
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`group relative bg-brand-brown/50 border border-brand-gold/10 rounded-lg overflow-hidden hover:border-brand-gold/40 transition-all duration-400 ${i === 4 ? "md:col-span-2 lg:col-span-1" : ""}`}
            >
              {/* Image */}
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-brand-darkbrown/60" />
              </div>

              {/* Step Number + Icon */}
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-brand-gold rounded-full">
                  <span className="font-display text-brand-darkbrown text-xs font-bold">{step.number}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${step.icon} text-brand-gold text-xl`} />
                  </div>
                  <h3 className="font-serif text-xl text-brand-cream font-bold">{step.title}</h3>
                </div>
                <p
                  className="font-sans text-sm text-brand-cream/60 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: step.desc }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Quote */}
        <div className="mt-16 text-center border-t border-brand-gold/15 pt-12">
          <blockquote className="font-serif text-2xl md:text-3xl text-brand-cream/90 italic max-w-3xl mx-auto leading-relaxed">
            "We don't make furniture. We create heirlooms that your grandchildren will cherish."
          </blockquote>
          <p className="font-display text-brand-gold text-xs tracking-widest uppercase mt-6">
            — M. Hayat &amp; Co., Est. 1870
          </p>
        </div>
      </div>
    </section>
  );
}
